import { buildR2AssetsImageUrl } from "@saas/shared/lib/image-url-builder";
import type { MusicFormValues } from "./types";

/**
 * Examples 区的样片：全部在本页工作台用 Standard（V6）出的，每单 2 首挑 1 首，
 * 封面是出歌时自带的。文件从生成库复制到 assets 桶 music/examples/ 下，别指向用户素材地址
 * （用户删素材是硬删）。values 就是出这首歌时的表单，「Use these settings」原样回填。
 */
export interface MusicShowcaseSong {
	id: string;
	/** 音轨标题：Simple 模式表单没有标题，用出歌时 AI 起的 */
	title: string;
	values: MusicFormValues;
	audioUrl: string;
	coverUrl: string;
	/** 秒 */
	duration: number;
	/** 同一首歌点「Make video」出的可视化视频 */
	videoUrl?: string;
}

export const MUSIC_SHOWCASE_UPLOADED_AT = "2026-11-03";

const file = (name: string) =>
	buildR2AssetsImageUrl(`music/examples/v1/${name}`);

const song = (
	id: string,
	title: string,
	duration: number,
	values: Partial<MusicFormValues> & Pick<MusicFormValues, "mode">,
): MusicShowcaseSong => ({
	id,
	title,
	duration,
	audioUrl: file(`${id}.mp3`),
	coverUrl: file(`${id}.jpg`),
	videoUrl: file(`${id}.mp4`),
	values: {
		model: "V6",
		title: null,
		prompt: null,
		style: null,
		lyrics: null,
		instrumental: false,
		vocalGender: null,
		...values,
	},
});

// 11/03 本页出的 4 单（Standard，每单 12 积分），每单挑 1 首；歌词是自己写的
export const musicShowcaseSongs: MusicShowcaseSong[] = [
	song("glass-lemon", "Glass Lemon", 148, {
		mode: "custom",
		title: "Glass Lemon",
		style: "upbeat dance pop, four-on-the-floor, bright synth plucks, stacked chorus harmonies, glossy mix",
		vocalGender: "f",
		lyrics: `[Verse 1]
Morning light on the kitchen tile
You left a note and a crooked smile
Lemon slices in a glass of ice
Sour and sweet, I'll take it twice

[Pre-Chorus]
Turn it up, let the windows shake
Every little thing is mine to make

[Chorus]
Glass lemon, shining in the sun
Squeeze it till the summer's done
Glass lemon, bright and bitter too
Every drop reminds me of you

[Verse 2]
Bike bells ringing down the avenue
Paper planes in a sky of blue
Spill the secrets, let the music play
Golden hours never fade away

[Chorus]
Glass lemon, shining in the sun
Squeeze it till the summer's done
Glass lemon, bright and bitter too
Every drop reminds me of you

[Bridge]
Crack it open, let it pour
Sweeter than the night before

[Chorus]
Glass lemon, shining in the sun
Squeeze it till the summer's done`,
	}),
	song("long-way-home", "The Long Way Home to You", 200, {
		mode: "simple",
		prompt: "A warm country song about driving home at sunset with an acoustic guitar and a male voice",
	}),
	song("rainy-desk", "Rainy Desk", 168, {
		mode: "custom",
		title: "Rainy Desk",
		style: "lo-fi hip hop, dusty vinyl crackle, mellow Rhodes chords, soft boom-bap drums, rain ambience, 75 BPM",
		instrumental: true,
	}),
	song("corner-code", "Corner Code", 112, {
		mode: "custom",
		title: "Corner Code",
		style: "boom-bap rap, gritty male vocal, cold piano stabs, chantable hook",
		vocalGender: "m",
		lyrics: `[Verse 1]
Streetlight hum on the corner block
Notebook full and a broken clock
Mama said keep your head held high
Write it down when the nights run dry
Bus stop bench is my studio
Every rhyme is a seed to grow

[Hook]
That's the corner code, we don't fold
Keep it real when the wind blows cold
That's the corner code, stories told
Turn the concrete into gold

[Verse 2]
Cold piano and a gritty snare
Dreams so loud you can feel them there
Little brother watching every move
Show him something that he can prove
Never borrowed what I couldn't give
Writing lines so the block can live

[Hook]
That's the corner code, we don't fold
Keep it real when the wind blows cold
That's the corner code, stories told
Turn the concrete into gold`,
	}),
];
